<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { UiButton, UiDialog, UiPillSelect, UiText } from "@shopexperts/shared";
import {
  SEND_MESSAGE_KEYMAP_STORAGE_KEY,
  isSendMessageKeymap,
  type SendMessageKeymap,
} from "./chatComposerSettings";

const isOpen = defineModel<boolean>("open", { default: false });
const keymap = defineModel<SendMessageKeymap>("keymap", { default: "enter" });

const keymapDraft = ref<SendMessageKeymap>(keymap.value);

const sendMessageKeymapOptions = [
  { value: "enter", label: "Enter" },
  { value: "shift-enter", label: "Shift + Enter" },
  { value: "ctrl-enter", label: "Cmd/Ctrl + Enter" },
  { value: "none", label: "None" },
] satisfies Array<{ value: SendMessageKeymap; label: string }>;

const keymapDraftModel = computed({
  get: () => keymapDraft.value,
  set: (value: string | string[] | undefined) => {
    const next = Array.isArray(value) ? value[0] : value;
    if (!isSendMessageKeymap(next)) return;

    keymapDraft.value = next;
  },
});

watch(isOpen, (open) => {
  if (open) keymapDraft.value = keymap.value;
});

function closeSettings() {
  keymapDraft.value = keymap.value;
  isOpen.value = false;
}

function saveSettings() {
  keymap.value = keymapDraft.value;
  window.localStorage.setItem(SEND_MESSAGE_KEYMAP_STORAGE_KEY, keymapDraft.value);
  isOpen.value = false;
}
</script>

<template>
  <UiDialog v-model="isOpen" title="Composer settings" size="lg" @close="closeSettings">
    <div class="flex flex-col gap-8">
      <section class="flex flex-col">
        <UiText
          as="h6"
          variant="caption"
          class="mb-2 text-[11px] font-semibold tracking-[0.14em] text-se-gray-600"
        >
          Keyboard
        </UiText>

        <div class="flex flex-col divide-y divide-se-gray-200">
          <div class="flex items-center justify-between gap-6 py-3.5 first:pt-2">
            <div class="min-w-0 flex flex-col gap-0.5">
              <UiText as="p" variant="p" class="font-medium text-se-dark">
                Send message
              </UiText>
              <UiText as="p" variant="p-small" class="text-se-gray-700">
                The shortcut that submits the current draft.
              </UiText>
            </div>
            <UiPillSelect
              v-model="keymapDraftModel"
              :options="sendMessageKeymapOptions"
              size="md"
              content-class="min-w-[12rem]"
              aria-label="Send message keymap"
            />
          </div>
        </div>
      </section>
    </div>

    <template #footer>
      <div class="-mx-8 -mb-8 flex items-center justify-end gap-2 border-t border-se-gray-200 bg-se-gray-50/60 px-8 py-4 rounded-b-2xl">
        <UiButton variant="outline" label="Cancel" @click="closeSettings" />
        <UiButton variant="primary" label="Save settings" @click="saveSettings" />
      </div>
    </template>
  </UiDialog>
</template>
